import React from "react";
import {
  CBadge,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CProgress,
  CProgressBar,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilCalendar,
  cilHeart,
  cilInfo,
  cilMoodGood,
  cilSpeedometer,
} from "@coreui/icons";
import "./RiskScoreModal.scss";


const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Unavailable";
  }

  if (typeof value === "number") {
    return Number(value.toFixed(2)).toString();
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return value;
};

const formatDate = (value) => {
  if (!value) return "Unavailable";

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Unavailable"
    : date.toLocaleString(undefined, {
        dateStyle: "medium",
        // timeStyle: "short",
      });
};

const riskTone = (value = "") =>
  String(value).toLowerCase().replace(/\s+/g, "-");

const progressValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number)
    ? Math.min(100, Math.max(0, number))
    : 0;
};

const hasValue = (value) =>
  value !== null && value !== undefined && value !== "";

const formatLabel = (value) =>
  String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

function Field({ label, value, icon }) {
  return (
    <div className="risk-detail-field">
      {icon && (
        <span className="risk-detail-field-icon">
          <CIcon icon={icon} />
        </span>
      )}
      <div>
        <span>{label}</span>
        <strong>{formatValue(value)}</strong>
      </div>
    </div>
  );
}

function SectionTitle({ icon, title, subtitle, tone = "purple" }) {
  return (
    <div className={`risk-section-title ${tone}`}>
      <span className="risk-section-icon">
        <CIcon icon={icon} />
      </span>
      <div>
        <h6>{title}</h6>
        {subtitle && <small>{subtitle}</small>}
      </div>
    </div>
  );
}

function ScoreProgress({ value, tone = "purple" }) {
  return (
    <CProgress className={`risk-score-progress ${tone}`} height={7}>
      <CProgressBar value={progressValue(value)} />
    </CProgress>
  );
}

function RiskParameter({ label, category, score }) {
  return (
    <div className="risk-parameter-card">
      <div className="risk-parameter-top">
        <span>{label}</span>
        {category ? (
          <CBadge className={`risk-status-badge tone-${riskTone(category)}`}>
            {category}
          </CBadge>
        ) : (
          <span className="risk-unavailable-badge"></span>
        )}
      </div>
      <div className="risk-parameter-bottom">
        <strong>{formatValue(score)}</strong>
        <span>Risk score</span>
      </div>
      {hasValue(score) && <ScoreProgress value={score} />}
    </div>
  );
}

function SummaryMetric({ label, value, suffix = "" }) {
  return (
    <div className="risk-summary-metric">
      <span>{label}</span>
      <strong>
        {formatValue(value)}
        {hasValue(value) && suffix}
      </strong>
    </div>
  );
}

export default function RiskScoreModal({ user, onClose,response,
  isLoading,
  isError, }) {

  const targetId = user?.identifier ?? user?.id ?? user?.userId;

const record = Array.isArray(response)
  ? response.find(
      (item) => String(item.userId) === String(targetId)
    )
  : response?.data && !Array.isArray(response.data)
    ? response.data
    : response;

  const overallTone = riskTone(
    record?.overallRiskLevel || record?.riskCategory || "unknown"
  );

  const healthParameters = [
    { label: "Wellness", category: record?.wellnessCategory, score: record?.wellnessRisk },
    { label: "Stress", category: record?.stressCategory, score: record?.stressRisk },
    { label: "Blood Pressure", category: record?.bpCategory, score: record?.bpRisk },
    { label: "Heart Rate", category: record?.heartRateCategory, score: record?.heartRateRisk },
    { label: "Oxygen", category: record?.oxygenCategory, score: record?.oxygenRisk },
    { label: "HRV", category: null, score: record?.hrvRisk },
    { label: "HbA1c", category: record?.hba1cCategory, score: record?.hba1cRisk },
  ];

  const allStoredFields = record
    ? Object.entries(record).filter(
        ([key]) => key !== "riskScoring" && key !== "__typename"
      )
    : [];

  return (
    <CModal show onClose={onClose} size="lg" className="risk-score-modal">
      <CModalHeader closeButton>
        <div>
          <div className="risk-score-eyebrow">DAILY WELLNESS</div>
          <CModalTitle>Risk Score Details</CModalTitle>
        </div>
      </CModalHeader>

      <CModalBody>
        {isLoading ? (
          <div className="risk-score-state">
            <CSpinner size="sm" /> Loading risk details...
          </div>
        ) : isError ? (
          <div className="risk-score-state risk-score-error">
            Unable to load risk details.
          </div>
        ) : !record ? (
          <div className="risk-score-state">
            No saved risk score found for this user today.
          </div>
        ) : (
          <div className="risk-dashboard">
            {/* <div className="risk-user-strip">
              <div className="risk-score-avatar">
                {(record.name || user?.name || "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="risk-user-info">
                <strong>{record.name || user?.name || "User"}</strong>
                <span>{record.email || user?.email || "Email unavailable"}</span>
              </div>
              <div className="risk-date-info">
                <span><CIcon icon={cilCalendar} /> Scoring date</span>
                <strong>{formatDate(record.startDateRange)}</strong>
              </div>
            </div> */}

            <section className={`risk-overall-card tone-${overallTone}`}>
              <div className="risk-overall-main">
                <span className="risk-overall-label">Overall risk score</span>
                <div className="risk-overall-score">
                  {formatValue(record.overallRiskScore)}
                  <small>/ 100</small>
                </div>
                <ScoreProgress value={record.overallRiskScore} tone="overall" />
              </div>
              <div className="risk-overall-status">
                <span>Overall risk level</span>
                <CBadge className={`risk-status-badge large tone-${overallTone}`}>
                  {record.overallRiskLevel || record.riskCategory || "Unknown"}
                </CBadge>
              </div>
              <div className="risk-overall-meta">
                <SummaryMetric label="Priority" value={record.overallPriority} />
                <SummaryMetric label="Coverage" value={record.overallRiskCoverage} suffix="%" />
              </div>
            </section>

            <section className="risk-dashboard-section">
              <SectionTitle
                icon={cilHeart}
                title="Health Risk"
                subtitle="Health parameters and their individual risk scores"
                tone="health"
              />

              <div className="risk-section-summary">
                <SummaryMetric label="Health score" value={record.healthRiskScore} />
                <SummaryMetric label="Risk level" value={record.healthRiskLevel || "Unknown"} />
                <SummaryMetric label="Trend" value={record.healthTrend} />
                <SummaryMetric label="Coverage" value={record.healthRiskCoverage} suffix="%" />
              </div>
              <div className="risk-health-details">
                <Field
                  label="Last health check"
                  value={formatDate(record.lastHealthCheckDate)}
                />
              </div>

              <div className="risk-parameter-grid">
                {healthParameters.map((parameter) => (
                  <RiskParameter key={parameter.label} {...parameter} />
                ))}
              </div>
            </section>

            <section className="risk-dashboard-section">
              <SectionTitle
                icon={cilMoodGood}
                title="Mood Risk"
                subtitle="Mood assessment, score and recent status"
                tone="mood"
              />

              <div className="risk-mood-layout">
                <div className="risk-mood-score-card">
                  <span>Mood risk score</span>
                  <strong>
                    {formatValue(record.moodRiskScore)}
                    <small>/ 100</small>
                  </strong>
                  <ScoreProgress value={record.moodRiskScore} tone="mood" />
                  <CBadge className={`risk-status-badge tone-${riskTone(record.moodRiskLevel)}`}>
                    {record.moodRiskLevel || "Unknown"}
                  </CBadge>
                </div>

                <div className="risk-mood-details">
                  <Field label="Mood score" value={record.moodScore} />
                  <Field label="Mood status" value={record.moodStatus} />
                  <Field label="Mood trend" value={record.moodTrend} />
                  <Field label="Last mood check" value={formatDate(record.lastMoodCheckDate)} />
                </div>
              </div>
            </section>

            <section className="risk-dashboard-section">
              <SectionTitle
                icon={cilSpeedometer}
                title="Engagement & Data Coverage"
                subtitle="How much health and mood information was available"
                tone="engagement"
              />

              <div className="risk-engagement-grid">
                <SummaryMetric
                  label="Health engagement"
                  value={record.healthEngagementScore}
                />
                <SummaryMetric
                  label="Health engagement level"
                  value={record.healthEngagementLevel}
                />
                <SummaryMetric
                  label="Mood engagement"
                  value={record.moodEngagement}
                />
                <SummaryMetric
                  label="Mood engagement level"
                  value={record.moodEngagementLevel}
                />
                <SummaryMetric
                  label="Overall engagement"
                  value={record.overallEngagement}
                />
                <SummaryMetric
                  label="Engagement coverage"
                  value={record.engagementCoverage}
                  suffix="%"
                />
              </div>
            </section>

            <section className="risk-message-card">
              <span className="risk-message-icon"><CIcon icon={cilInfo} /></span>
              <div>
                <strong>Risk assessment</strong>
                <p>{record.riskMessage || "No risk message available."}</p>
              </div>
            </section>
          </div>
        )}
      </CModalBody>
    </CModal>
  );
}